import { bitbucketClient } from "./bitbucketCore";

// Repository variables API
export const getRepositoryVariables = async (workspace, repoSlug) => {
  try {
    console.log(`Fetching variables for ${workspace}/${repoSlug}`);
    let url = `/repositories/${workspace}/${repoSlug}/pipelines_config/variables/?pagelen=100`;
    let allVariables = [];
    let hasNextPage = true;
    let pageCount = 0;
    const MAX_PAGES = 20; // Safety limit to prevent infinite loops
    let totalSize = null; // Will store the total variables count from API

    // Handle pagination by fetching all pages
    while (hasNextPage && pageCount < MAX_PAGES) {
      pageCount++;
      console.log(`Fetching variables page ${pageCount} from: ${url}`);

      const response = await bitbucketClient.get(url);

      if (!response.data || !response.data.values) {
        console.warn(`No values returned for page ${pageCount}`);
        break;
      }

      // Store total size from first response
      if (totalSize === null && response.data.size !== undefined) {
        totalSize = response.data.size;
        console.log(`API reports total of ${totalSize} variables available`);
      }

      console.log(
        `Page ${pageCount} returned ${response.data.values.length} variables`
      );
      allVariables = [...allVariables, ...response.data.values];

      // Stop if we've collected all variables based on the reported size
      if (totalSize !== null && allVariables.length >= totalSize) {
        console.log(
          `Collected all ${totalSize} variables, stopping pagination`
        );
        break;
      }

      // Check if there's a next page
      if (response.data.next && response.data.next !== url) {
        // Use the full URL from the response for the next page
        url = response.data.next;
      } else {
        hasNextPage = false;
      }
    }

    // Log total variables found
    console.log(
      `Total variables found for ${workspace}/${repoSlug}: ${allVariables.length}`
    );

    // Deduplicate by key, keeping the most recent variable when duplicates exist
    const uniqueVariablesMap = new Map();

    // Process in reverse order to prioritize later items (more recent in paginated results)
    for (let i = allVariables.length - 1; i >= 0; i--) {
      const variable = allVariables[i];
      // Only add if this key isn't already in our map
      if (!uniqueVariablesMap.has(variable.key)) {
        uniqueVariablesMap.set(variable.key, variable);
      }
    }

    // Convert back to array
    const deduplicatedVariables = Array.from(uniqueVariablesMap.values());
    console.log(
      `Deduplicated variables for ${workspace}/${repoSlug}: ${deduplicatedVariables.length}`
    );

    return deduplicatedVariables;
  } catch (error) {
    console.error(
      `Error fetching repository variables for ${workspace}/${repoSlug}:`,
      error
    );
    throw error;
  }
};

export const createRepositoryVariable = async (
  workspace,
  repoSlug,
  variable
) => {
  try {
    const response = await bitbucketClient.post(
      `/repositories/${workspace}/${repoSlug}/pipelines_config/variables/`,
      variable
    );
    return response.data;
  } catch (error) {
    console.error("Error creating repository variable:", error);
    throw error;
  }
};

export const updateRepositoryVariable = async (
  workspace,
  repoSlug,
  uuid,
  variable
) => {
  try {
    const response = await bitbucketClient.put(
      `/repositories/${workspace}/${repoSlug}/pipelines_config/variables/${uuid}`,
      variable
    );
    return response.data;
  } catch (error) {
    console.error("Error updating repository variable:", error);
    throw error;
  }
};

export const deleteRepositoryVariable = async (workspace, repoSlug, uuid) => {
  try {
    await bitbucketClient.delete(
      `/repositories/${workspace}/${repoSlug}/pipelines_config/variables/${uuid}`
    );
    return true;
  } catch (error) {
    console.error("Error deleting repository variable:", error);
    throw error;
  }
};

// Bulk operations
export const bulkCreateVariables = async (workspace, repoSlug, variables) => {
  const results = [];
  for (const variable of variables) {
    try {
      const result = await createRepositoryVariable(
        workspace,
        repoSlug,
        variable
      );
      results.push({ success: true, data: result, variable });
    } catch (error) {
      results.push({ success: false, error, variable });
    }
  }
  return results;
};

export const bulkUpdateVariables = async (workspace, repoSlug, variables) => {
  const results = [];
  for (const variable of variables) {
    try {
      const result = await updateRepositoryVariable(
        workspace,
        repoSlug,
        variable.uuid,
        variable
      );
      results.push({ success: true, data: result, variable });
    } catch (error) {
      results.push({ success: false, error, variable });
    }
  }
  return results;
};

export const bulkDeleteVariables = async (workspace, repoSlug, uuids) => {
  const results = [];
  for (const uuid of uuids) {
    try {
      await deleteRepositoryVariable(workspace, repoSlug, uuid);
      results.push({ success: true, uuid });
    } catch (error) {
      results.push({ success: false, error, uuid });
    }
  }
  return results;
};

// Alternative approach that doesn't rely on delete-then-create
export const copyVariablesToRepositories = async (
  sourceWorkspace,
  sourceRepoSlug,
  targetRepositories,
  variables,
  progressCallback = null
) => {
  // Track summary statistics
  const summary = {
    totalCreated: 0,
    totalFailed: 0,
    totalRepositories: targetRepositories.length,
    completedRepositories: 0,
    currentRepository: null,
    currentVariable: null,
  };

  // Stores all processed results
  const allResults = [];

  // Process repositories sequentially to avoid overwhelming the API
  for (const targetRepo of targetRepositories) {
    // Update progress with current repository
    summary.currentRepository = targetRepo;
    summary.completedRepositories += 1;

    if (progressCallback) {
      progressCallback({
        ...summary,
        status: `Processing repository ${summary.completedRepositories}/${summary.totalRepositories}: ${targetRepo}`,
      });
    }

    console.log(`Processing repository: ${targetRepo}`);
    const repoResults = [];

    try {
      // First get all existing variables to find any with matching keys
      let existingVariables;
      try {
        existingVariables = await getRepositoryVariables(
          sourceWorkspace,
          targetRepo
        );
        console.log(
          `Found ${existingVariables.length} existing variables in ${targetRepo}`
        );
      } catch (error) {
        console.error(
          `Error fetching existing variables for ${targetRepo}`,
          error
        );
        existingVariables = [];
      }

      // Process each variable sequentially
      for (const variable of variables) {
        try {
          // Update progress with current variable
          summary.currentVariable = variable.key;

          if (progressCallback) {
            progressCallback({
              ...summary,
              status: `Repository ${summary.completedRepositories}/${summary.totalRepositories}: ${targetRepo} - Copying: ${variable.key}`,
            });
          }

          console.log(`Processing variable: ${variable.key}`);

          const variableData = {
            key: variable.key,
            value: variable.value,
            secured: variable.secured,
          };

          // Step 1: Look for any existing variable with the same key and delete it
          const existingVar = existingVariables.find(
            (v) => v.key === variable.key
          );

          if (existingVar) {
            console.log(
              `Found existing variable ${variable.key}, deleting it first`
            );
            try {
              await deleteRepositoryVariable(
                sourceWorkspace,
                targetRepo,
                existingVar.uuid
              );
            } catch (deleteError) {
              console.error(
                `Error deleting variable ${variable.key}:`,
                deleteError
              );
              // Continue anyway, the create might still work
            }
          }

          // Step 2: Create the variable fresh
          await createRepositoryVariable(
            sourceWorkspace,
            targetRepo,
            variableData
          );

          summary.totalCreated++;

          // Add success result
          repoResults.push({
            success: true,
            variable: variableData,
            repository: targetRepo,
          });
        } catch (variableError) {
          console.error(
            `Failed to process variable ${variable.key}:`,
            variableError
          );

          repoResults.push({
            success: false,
            error: variableError,
            variable: { key: variable.key },
            repository: targetRepo,
          });

          summary.totalFailed++;
        }
      }
    } catch (repoError) {
      console.error(`Error accessing repository ${targetRepo}:`, repoError);

      // Mark all remaining variables as failed
      for (const variable of variables) {
        repoResults.push({
          success: false,
          error: { message: "Could not access repository variables" },
          variable: { key: variable.key },
          repository: targetRepo,
        });
        summary.totalFailed++;
      }
    }

    allResults.push({
      repository: targetRepo,
      results: repoResults,
    });
  }

  // Return both detailed results and summary
  return {
    results: allResults,
    summary,
  };
};

const variablesApi = {
  getRepositoryVariables,
  createRepositoryVariable,
  updateRepositoryVariable,
  deleteRepositoryVariable,
  bulkCreateVariables,
  bulkUpdateVariables,
  bulkDeleteVariables,
  copyVariablesToRepositories,
};

export default variablesApi;
